/*
 * PW Offers — promotional offer engine (Puffweiser port).
 *
 * Ported from a sibling store's offers engine. Offer config (qualifying
 * variants, gift pool, bundle components, quantities) lives in Shopify
 * metaobjects/metafields and is rendered server-side by
 * sections/pw-offer-data.liquid as a JSON island. No campaign specifics
 * are hardcoded here.
 *
 * WHAT CHANGED vs. the original build (see OFFERS-SETUP.md):
 *
 *  1. ATOMIC ADD is the primary flow. Clicking any add-to-cart button for a
 *     qualifying product sends the paid line AND its reward line(s) in ONE
 *     /cart/add.js call. The original build relied on a background reconciler to
 *     add rewards after the fact, which proved unreliable; here the reconciler
 *     NEVER adds — it only removes orphans and caps quantities.
 *
 *  2. COLLECTION-GRID SUPPORT. The engine intercepts add-to-cart clicks in the
 *     GemPages product grid, so an offer works straight off the collection page.
 *
 *  SCOPE. The engine is inert unless the page renders a #PWOfferData island for
 *  a fully-configured offer, and even then it only ever intercepts a click whose
 *  variant the offer explicitly names (an entry in qualifying_variants, or a
 *  member of the offer's own collection). Every other add-to-cart on the store -
 *  notably the theme's `bogo_offer` quantity-2 forms and the product-bundle
 *  sections - is left completely alone.
 *
 *  3. THEME BINDINGS. Cart UI refresh uses this theme's API
 *     (theme.pubsub + <cart-drawer>), not Dawn's cart-notification/cart-items.
 *
 * Public API:
 *   window.PWOffers.version      — verify the deployed build
 *   window.PWOffers.debug()      — dump cart + offer state to the console
 *   window.PWOffers.syncCart()   — force a reconcile pass
 *   window.PWOffers.buyNow(id)   — permalink straight to checkout
 *   window.PWOffers.buyBundleNow(ids, qty)
 */
(function () {
  if (window.PWOffers) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var PARAM_KEY = 'offer';
  var CART_ATTR_KEY = '_pw_active_offer';
  var SESSION_KEY = 'pw_active_offer';
  var CONFIG_CACHE_PREFIX = 'pw_offer_config_';
  var DATA_ISLAND_ID = 'PWOfferData';

  /* Add-to-cart buttons we intercept: the GemPages grid/PDP button and this
     theme's own product form button.

     [data-product-bundle-submit] is deliberately NOT here. That button belongs
     to the theme's own product-bundle sections, which post several lines at
     once; intercepting it replaced the whole bundle with a single line. */
  var ATC_SELECTOR = '.gp-button-atc, button[name="add"]';

  /* ...and contexts we never intercept in, even on an offer page. These render
     add-to-cart buttons for products other than the one the shopper is looking
     at, so a click here is never a click on the offer. */
  var SKIP_WITHIN_SELECTOR = [
    'cart-drawer',
    'cart-items',
    'quick-view',
    '.quick-add',
    'product-recommendations',
    'compact-product-bundle',
    'floating-product-bundle'
  ].join(',');

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* Verbose diagnostics. Enable by visiting any page with ?pw_debug=1
     (persists for the session) — then watch the console. */
  function hlog() {
    if (!PWOffers || !PWOffers.debugEnabled) return;
    var args = ['[PWOffers]'].concat([].slice.call(arguments));
    console.info.apply(console, args);
  }

  function jsonFetch(path, options) {
    return fetch(ROOT.replace(/\/$/, '') + path, options).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return {}; }).then(function (body) {
          return Promise.reject(Object.assign({ status: res.status }, body));
        });
      }
      return res.json();
    });
  }

  function postJSON(path, body) {
    return jsonFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function getCart() {
    return jsonFetch('/cart.js');
  }

  function addItems(items) {
    return postJSON('/cart/add.js', { items: items }).then(function (res) {
      PWOffers._dirty = true;
      return res;
    });
  }

  function changeLine(key, quantity) {
    return postJSON('/cart/change.js', { id: key, quantity: quantity }).then(function (res) {
      PWOffers._dirty = true;
      return res;
    });
  }

  function updateCartAttributes(attributes) {
    return postJSON('/cart/update.js', { attributes: attributes });
  }

  /* ---- offer config: read the current page's data island, or fall back
     to whatever was cached the last time this offer's data island was seen
     (needed on pages like /cart that never render one). ---- */

  function readDataIsland(doc) {
    var el = (doc || document).getElementById(DATA_ISLAND_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function cacheOfferConfig(config) {
    if (!config || !config.offer_id) return;
    try {
      sessionStorage.setItem(CONFIG_CACHE_PREFIX + config.offer_id, JSON.stringify(config));
    } catch (e) {
      /* storage unavailable, ignore */
    }
  }

  function getCachedOfferConfig(offerId) {
    try {
      var raw = sessionStorage.getItem(CONFIG_CACHE_PREFIX + offerId);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  /* Re-render the offer's data section via the Section Rendering API for
     fresh inventory. JSON-template sections must be requested by their
     dynamic id (config.section_id, e.g. "template--123__pw-offer-data"),
     against a page URL that actually renders them (config._source_path). */
  function refreshDataIsland(config) {
    if (!config || !config.section_id) return Promise.resolve(null);
    var path = config._source_path || window.location.pathname;
    var url = path + '?section_id=' + encodeURIComponent(config.section_id);
    return fetch(url)
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return readDataIsland(doc);
      })
      .catch(function () { return null; });
  }

  /* ---- highest-available-inventory gift selection ---- */

  function availableGiftPool(config, fresh) {
    var pool = (fresh && fresh.gift_pool) || config.gift_pool || [];
    var candidates = pool.filter(function (v) {
      return v.available && (v.inventory_policy === 'continue' || v.inventory_quantity > 0);
    });
    candidates.sort(function (a, b) { return b.inventory_quantity - a.inventory_quantity; });
    return candidates;
  }

  /* ---- variant resolution ----

     The Liquid product form is the reliable source when GemPages renders one.
     Some grid, quick-add and slider layouts add via their own AJAX with no
     form (or move the button out of it), so fall back to the variant GemPages
     stamps on its own custom elements before giving up. */

  function readJsonAttr(el, name) {
    var raw = el && el.getAttribute && el.getAttribute(name);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function resolveVariantId(button) {
    var form = button.closest('form');
    var input = form && form.querySelector('[name="id"]');
    if (input && input.value) return input.value;

    var btnWrap = button.closest('gp-product-button');
    var fromButton = readJsonAttr(btnWrap, 'gp-data');
    if (fromButton && fromButton.variantID) {
      hlog('variant resolved from gp-product-button', fromButton.variantID);
      return fromButton.variantID;
    }

    var card = button.closest('gp-product');
    var ctx = readJsonAttr(card, 'gp-context') || readJsonAttr(card, 'gp-data');
    if (ctx && ctx.variantSelected && ctx.variantSelected.id) {
      hlog('variant resolved from gp-product context', ctx.variantSelected.id);
      return ctx.variantSelected.id;
    }

    return null;
  }

  /* ---- qualification ---- */

  /* An offer that grants nothing must never take a click: intercepting it would
     replace the theme's own add (quantity 2 on a `bogo_offer` product) with a
     quantity-1 add and no reward. A half-configured metaobject entry then reads
     as "this store's BOGO is broken". */
  function offerGrantsReward(config) {
    return !!(config && (config.same_variant_gift || config.inventory_gift_enabled));
  }

  function variantQualifies(config, variantId) {
    if (!config) return false;
    var vid = parseInt(variantId, 10);

    /* An explicit variant list always wins when the merchant set one. */
    if ((config.qualifying_variant_ids || []).indexOf(vid) !== -1) return true;

    if (!config.qualify_all_in_collection) return false;

    if (config.context === 'collection') {
      /* "Buy any 28g, get one free" spares the merchant listing hundreds of
         variants — but it means every product in THIS collection, not every
         button on the page. The id list comes from the data island, so an
         unrelated card (an upsell, a footer strip, another live bogo_offer
         product) falls through to the theme's own add-to-cart. */
      return (config.collection_variant_ids || []).indexOf(vid) !== -1;
    }

    /* On a PDP, only this page's own product qualifies, and only when that
       product is genuinely in the offer — a product that merely shares the
       template, or was reached through the offer collection's URL, does not. */
    return config.page_product_in_offer === true &&
      (config.page_product_variant_ids || []).indexOf(vid) !== -1;
  }

  /* Build the full line-item payload for one click: the paid unit followed by
     whichever rewards this offer grants. Returns a promise so the gift pool
     can be re-checked against live inventory first. */
  function buildOfferItems(config, variantId, quantity) {
    var qty = parseInt(quantity, 10) || 1;
    var vid = parseInt(variantId, 10);

    var items = [{ id: vid, quantity: qty }];

    if (config.same_variant_gift) {
      items.push({
        id: vid,
        quantity: (config.same_variant_gift_quantity || 1) * qty,
        properties: {
          _pw_auto_item: 'true',
          _pw_offer_id: config.offer_id,
          _pw_offer_role: 'same_product_bogo'
        }
      });
    }

    if (!config.inventory_gift_enabled) {
      if (!config.same_variant_gift) {
        hlog('offer grants NO reward: same_variant_gift and inventory_gift_enabled are both false',
             'offer_id=' + config.offer_id);
      }
      return Promise.resolve(items);
    }

    return refreshDataIsland(config).then(function (fresh) {
      var pool = availableGiftPool(config, fresh);
      if (!pool.length) {
        hlog('gift pool empty', 'raw=' + ((fresh && fresh.gift_pool) || config.gift_pool || []).length,
             'offer_id=' + config.offer_id);
        PWOffers.showMessage(config.unavailable_message || 'The complimentary gift is currently unavailable.');
        return items;
      }
      hlog('gift selected', pool[0].id, 'stock=' + pool[0].inventory_quantity);
      items.push({
        id: pool[0].id,
        quantity: config.inventory_gift_quantity || 1,
        properties: {
          _pw_auto_item: 'true',
          _pw_offer_id: config.offer_id,
          _pw_offer_role: 'shake_gift'
        }
      });
      return items;
    });
  }

  /* ---- reconciliation: remove orphaned rewards, cap reward quantities.
     This pass NEVER adds anything — adding is the click handler's job. ---- */

  function findPaidQualifyingItem(cart, config) {
    return cart.items.filter(function (item) {
      if (item.properties && item.properties._pw_auto_item) return false;
      return variantQualifies(config, item.variant_id);
    })[0] || null;
  }

  function lookupConfig(offerId, currentConfig) {
    if (currentConfig && currentConfig.offer_id === offerId) return currentConfig;
    return getCachedOfferConfig(offerId);
  }

  /* Remove reward items whose qualifying paid item is gone.
     Returns the set of removed line keys so later steps can skip them. */
  function removeOrphanedAutoItems(cart, currentConfig) {
    var autoItems = cart.items.filter(function (item) {
      return item.properties && item.properties._pw_auto_item === 'true';
    });

    var removedKeys = {};
    var work = Promise.resolve();
    autoItems.forEach(function (item) {
      if (item.properties._pw_offer_role === 'bundle_component') return;

      var config = lookupConfig(item.properties._pw_offer_id, currentConfig);
      if (!config) return; // config unknown this session — leave the line alone

      if (!findPaidQualifyingItem(cart, config)) {
        hlog('orphan reward removed', item.variant_id);
        removedKeys[item.key] = true;
        work = work.then(function () { return changeLine(item.key, 0); });
      }
    });
    return work.then(function () { return removedKeys; });
  }

  /* Enforce reward quantities and collapse duplicate reward lines that
     slipped through a race between two adds. */
  function normalizeAutoItems(cart, removedKeys, currentConfig) {
    var seen = {};
    var work = Promise.resolve();
    cart.items.forEach(function (item) {
      if (!(item.properties && item.properties._pw_auto_item === 'true')) return;
      if (removedKeys && removedKeys[item.key]) return;

      var role = item.properties._pw_offer_role;
      var offerId = item.properties._pw_offer_id;
      var config = lookupConfig(offerId, currentConfig);

      if (role !== 'bundle_component') {
        var dedupeKey = offerId + ':' + role;
        if (seen[dedupeKey]) {
          work = work.then(function () { return changeLine(item.key, 0); });
          return;
        }
        seen[dedupeKey] = true;
      }

      /* Cap the reward at what the offer actually grants.
         maximum_applications_per_order lets one order stack the reward N
         times (default 1). With no config we don't know what this offer grants,
         so leave the line alone rather than capping it to a guessed 1. */
      if (!config) {
        hlog('normalize: config unknown for offer', offerId, '- quantity left alone');
        return;
      }
      var apps = config.maximum_applications_per_order || 1;
      var maxQty = apps;
      if (role === 'shake_gift') maxQty = (config.inventory_gift_quantity || 1) * apps;
      if (role === 'same_product_bogo') maxQty = (config.same_variant_gift_quantity || 1) * apps;
      if (item.quantity > maxQty) {
        work = work.then(function () { return changeLine(item.key, maxQty); });
      }
    });
    return work;
  }

  /* ---- public engine ---- */

  var PWOffers = {
    version: '2.3.0-pw',
    debugEnabled: false,
    isOfferPage: false,
    _locked: false,
    _pendingSync: false,
    _dirty: false,
    _busy: false,

    /* The script loads on every page, so init decides how much of the engine to
       switch on. Anything that can change what a click or a cart write does is
       installed ONLY where an offer is actually configured; everywhere else the
       engine holds nothing but a cheap cart-update listener. */
    init: function () {
      this.detectDebug();
      this.isOfferPage = !!readDataIsland();
      hlog('init v' + this.version, 'offerPage=' + this.isOfferPage);

      if (this.isOfferPage) {
        this.warnIfUnscoped();
        this.injectStyles();
        this.captureCampaignMarker();
        this.cacheCurrentPageOfferConfig();
        this.hookAddToCart();
        this.hookOwnButtons();
      }

      /* Reconciliation still has to reach beyond the offer pages: the shopper
         can delete the paid item from the cart drawer anywhere on the store.
         The subscription itself costs nothing and fires only on a real cart
         change, and syncCart returns immediately when the cart holds no reward
         lines of ours. */
      this.hookPubSub();
      if (this.isOfferPage || this.hasActiveOffer() || this.onCartPage()) {
        this.syncCart();
      } else {
        hlog('idle: no offer on this page and none active this session');
      }
    },

    /* Loud on purpose — not behind ?pw_debug. Both of these leave an offer that
       looks configured but silently grants nothing on some or all of its
       products, and the storefront gives no other clue. */
    warnIfUnscoped: function () {
      var config = readDataIsland();
      if (!config || !config.qualify_all_in_collection) return;
      if (!config.offer_collection_handle) {
        console.warn('[PWOffers] offer "' + config.offer_id + '" qualifies all products in ' +
          'a collection, but names no collection. Set "Offer config from" on the PW Offer Data ' +
          'section, or link the offer via the collection custom.pw_offer metafield. ' +
          'Nothing on this page qualifies until then.');
      } else if (config.collection_ids_truncated) {
        console.warn('[PWOffers] collection "' + config.offer_collection_handle + '" has more ' +
          'products than Liquid can list in one render (50). Products past the first 50 will ' +
          'not get the reward - list them in the offer qualifying_variants field instead.');
      }
    },

    onCartPage: function () {
      return /\/cart(\/|$|\?)/.test(window.location.pathname + window.location.search) ||
        (document.body && document.body.classList.contains('template-cart'));
    },

    hasActiveOffer: function () {
      try {
        return !!sessionStorage.getItem(SESSION_KEY);
      } catch (e) {
        return false;
      }
    },

    detectDebug: function () {
      try {
        if (/[?&]pw_debug/.test(window.location.search)) {
          sessionStorage.setItem('pw_debug', '1');
        }
        this.debugEnabled = sessionStorage.getItem('pw_debug') === '1';
      } catch (e) {
        this.debugEnabled = /[?&]pw_debug/.test(window.location.search);
      }
    },

    debug: function () {
      var self = this;
      return getCart().then(function (cart) {
        var island = readDataIsland();
        var info = {
          version: self.version,
          isOfferPage: self.isOfferPage,
          activeOfferId: self.getActiveOfferId(cart),
          islandPresent: !!island,
          islandOfferId: island ? island.offer_id : null,
          islandContext: island ? island.context : null,
          qualifyAllInCollection: island ? !!island.qualify_all_in_collection : null,
          /* Why a click did or didn't get intercepted. */
          grantsReward: island ? offerGrantsReward(island) : null,
          offerCollectionHandle: island ? island.offer_collection_handle : null,
          scopedVariantCount: island
            ? (island.qualifying_variant_ids || []).length + (island.collection_variant_ids || []).length
            : null,
          collectionIdsTruncated: island ? !!island.collection_ids_truncated : null,
          pageProductInOffer: island ? island.page_product_in_offer : null,
          cartAttributes: cart.attributes,
          items: cart.items.map(function (i) {
            return { variant_id: i.variant_id, qty: i.quantity, props: i.properties };
          })
        };
        console.log('[PWOffers] status', info);
        return info;
      });
    },

    injectStyles: function () {
      if (document.getElementById('PWOffersStyles')) return;
      var style = document.createElement('style');
      style.id = 'PWOffersStyles';
      style.textContent =
        '.pw-offer-badge{display:inline-block;padding:.3em .8em;border-radius:999px;' +
        'font-size:1.1rem;font-weight:700;letter-spacing:.02em;text-transform:uppercase;}' +
        '.pw-offer-badge--gift{background:#1a7a3a;color:#fff;}' +
        '.pw-offer-badge--bundle{background:#2a4d8f;color:#fff;}' +
        '.pw-offer-badge--savings{background:#447A2D;color:#fff;}';
      document.head.appendChild(style);
    },

    captureCampaignMarker: function () {
      var params = new URLSearchParams(window.location.search);
      var offerParam = params.get(PARAM_KEY);
      var islandConfig = readDataIsland();

      /* Entering the offer flow = an explicit ?offer= param, OR landing on an
         offer collection page (only offer landing templates render the data
         island in a collection context). A PDP island alone does NOT activate
         — direct product visits must stay non-promotional. */
      var active = offerParam ||
        (islandConfig && islandConfig.context === 'collection' && islandConfig.offer_id) ||
        null;
      if (!active) {
        hlog('no activation on this page (no ?offer param, no collection island)');
        return;
      }
      hlog('offer activated:', active);
      this.setActiveOffer(active);
    },

    setActiveOffer: function (offerId) {
      try {
        sessionStorage.setItem(SESSION_KEY, offerId);
      } catch (e) { /* ignore */ }
      var attrs = {};
      attrs[CART_ATTR_KEY] = offerId;
      updateCartAttributes(attrs).catch(function () { /* non-fatal */ });
    },

    cacheCurrentPageOfferConfig: function () {
      var config = readDataIsland();
      if (config) {
        /* Remember which page rendered this island so inventory can be
           re-fetched later from pages (like /cart) that don't render one. */
        config._source_path = window.location.pathname;
        cacheOfferConfig(config);
      }
    },

    getActiveOfferId: function (cart) {
      var id = null;
      try {
        id = sessionStorage.getItem(SESSION_KEY);
      } catch (e) { /* ignore */ }
      /* Fallback: the marker also lives on the cart itself, which survives
         new tabs and cleared session storage. */
      if (!id && cart && cart.attributes && cart.attributes[CART_ATTR_KEY]) {
        id = cart.attributes[CART_ATTR_KEY];
        try {
          sessionStorage.setItem(SESSION_KEY, id);
        } catch (e) { /* ignore */ }
      }
      return id;
    },

    /* ---- our own grid buttons (sections/pw-offer-collection.liquid) ----

       These we render ourselves, so there is nothing to intercept and no form
       to inspect: the variant id is on the button, or on the card's variant
       <select> when the product has more than one. This path has no dependency
       on GemPages markup and cannot be broken by a GemPages republish. */

    hookOwnButtons: function () {
      document.addEventListener('click', function (event) {
        var button = event.target && event.target.closest && event.target.closest('[data-pw-add]');
        if (!button || button.disabled) return;

        event.preventDefault();

        var card = button.closest('[data-pw-card]');
        var select = card && card.querySelector('[data-pw-variant-select]');
        var variantId = (select && select.value) || button.getAttribute('data-pw-variant');
        if (!variantId) {
          hlog('pw button: no variant id on card');
          return;
        }

        var config = readDataIsland();

        /* No offer config on the page (metafield unset, or the PW Offer Data
           section was removed) - still add the item so the button is never
           dead, just without the reward. */
        if (!config) {
          hlog('pw button: no data island - plain add', variantId);
          PWOffers.addPlainToCart(button, variantId, 1);
          return;
        }

        if (!offerGrantsReward(config)) {
          hlog('pw button: offer grants no reward - plain add', variantId,
               'offer_id=' + config.offer_id);
          PWOffers.addPlainToCart(button, variantId, 1);
          return;
        }

        if (!variantQualifies(config, variantId)) {
          hlog('pw button: variant does not qualify - plain add', variantId,
               'context=' + config.context,
               'qualifyAll=' + !!config.qualify_all_in_collection);
          PWOffers.addPlainToCart(button, variantId, 1);
          return;
        }

        hlog('pw button: offer add', variantId);
        PWOffers.addOfferToCart(button, config, variantId, 1);
      });
    },

    /* Ordinary single-line add, used when this page's card doesn't qualify. */
    addPlainToCart: function (button, variantId, quantity) {
      var self = this;
      if (self._busy) return Promise.resolve();
      self._busy = true;
      if (button) { button.setAttribute('data-state', 'loading'); }

      return addItems([{ id: parseInt(variantId, 10), quantity: parseInt(quantity, 10) || 1 }])
        .then(function () { return self.refreshCartUI(button); })
        .catch(function (err) {
          console.error('[PWOffers] plain add failed', err);
          self.showMessage((err && err.description) || 'Sorry - that item could not be added. Please try again.');
        })
        .then(function () {
          self._busy = false;
          if (button) { button.setAttribute('data-state', 'idle'); }
        });
    },

    /* ---- collection-grid + PDP add-to-cart interception ----

       Capture-phase so we win the click before GemPages' own submit handler,
       which we then cancel — otherwise the customer would get GemPages' add
       AND ours. If anything about the click doesn't qualify we return without
       touching the event and the theme behaves exactly as before. */

    hookAddToCart: function () {
      document.addEventListener('click', function (event) {
        var button = event.target && event.target.closest && event.target.closest(ATC_SELECTOR);
        if (!button || button.disabled) return;

        /* A drawer, quick-add, quick-view or bundle button is never the offer's
           button, even on the offer's own page. */
        if (button.closest(SKIP_WITHIN_SELECTOR)) {
          hlog('bail: button lives in a context we never intercept', button);
          return;
        }

        var config = readDataIsland();
        if (!config) { hlog('bail: no data island on this page'); return; }
        if (config.offer_type === 'bundle_fixed') { hlog('bail: bundle_fixed uses its own button'); return; }

        /* Nothing to grant means nothing to take the click for — let the theme's
           own add (including its `bogo_offer` quantity of 2) run untouched. */
        if (!offerGrantsReward(config)) {
          hlog('bail: offer grants no reward', 'offer_id=' + config.offer_id);
          return;
        }

        var variantId = resolveVariantId(button);
        if (!variantId) { hlog('bail: could not resolve a variant id for this button', button); return; }

        if (!variantQualifies(config, variantId)) {
          hlog('bail: variant does not qualify', variantId,
               'context=' + config.context,
               'qualifyAll=' + !!config.qualify_all_in_collection,
               'namedVariants=' + (config.qualifying_variant_ids || []).length,
               'collectionVariants=' + (config.collection_variant_ids || []).length,
               'pageProductInOffer=' + config.page_product_in_offer);
          return;
        }
        hlog('intercepting click for variant', variantId);

        /* We own this click. */
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        /* Deliberately ignore the form's quantity input: on bogo_offer-tagged
           products the theme hardcodes it to 2, which would double the PAID
           units now that the free unit is its own line. */
        PWOffers.addOfferToCart(button, config, variantId, 1);
      }, true);
    },

    /* One atomic add: paid line + reward line(s) in a single /cart/add.js. */
    addOfferToCart: function (button, config, variantId, quantity) {
      var self = this;
      if (self._busy) return Promise.resolve();
      self._busy = true;

      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('data-state', 'loading');
      }

      return buildOfferItems(config, variantId, quantity).then(function (items) {
        hlog('adding', items.length, 'lines for offer', config.offer_id, items);
        return addItems(items);
      }).then(function () {
        return self.refreshCartUI(button);
      }).catch(function (err) {
        console.error('[PWOffers] offer add failed', err);
        self.showMessage((err && err.description) || 'Sorry — that item could not be added. Please try again.');
      }).then(function () {
        self._busy = false;
        if (button) {
          button.removeAttribute('aria-busy');
          button.setAttribute('data-state', 'idle');
        }
      });
    },

    /* ---- cart UI refresh, using THIS theme's API ----
       Mirrors what the theme's own ProductForm does after a successful add:
       fetch the cart, attach the re-rendered cart sections, publish cartUpdate
       (updates the mini cart, main cart and cart count), then open the
       drawer. */

    refreshCartUI: function (focusElement) {
      var self = this;
      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
      );

      var drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.getAttribute('data-section-id') &&
          sections.indexOf(drawer.getAttribute('data-section-id')) === -1) {
        sections.push(drawer.getAttribute('data-section-id'));
      }
      Array.prototype.forEach.call(
        document.querySelectorAll('cart-items[data-section-id]'),
        function (el) {
          var id = el.getAttribute('data-section-id');
          if (sections.indexOf(id) === -1) sections.push(id);
        }
      );

      var base = ROOT.replace(/\/$/, '') + '/';
      var request = sections.length
        ? fetch(base + '?sections=' + sections.join(','))
            .then(function (res) { return res.ok ? res.json() : {}; })
            .catch(function () { return {}; })
        : Promise.resolve({});

      return request.then(function (renderedSections) {
        return getCart().then(function (cart) {
          cart.sections = renderedSections;
          self._dirty = false;

          if (window.theme && theme.pubsub && theme.pubsub.publish) {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'pw-offers',
              cart: cart
            });
          } else {
            /* No pubsub (very early load): let the drawer re-fetch itself. */
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
          }

          if (drawer && typeof drawer.show === 'function') {
            drawer.show(focusElement || null);
          }
          return cart;
        });
      }).catch(function (e) {
        console.error('[PWOffers] refresh failed', e);
        /* Last resort so the customer still sees their cart update. */
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      });
    },

    hookPubSub: function () {
      if (window.theme && theme.pubsub && theme.pubsub.subscribe) {
        theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
          if (event && event.source === 'pw-offers') return;
          PWOffers.scheduleSync();
        });
      }
    },

    /* NOTE: earlier builds also wrapped window.fetch store-wide to catch cart
       writes made by other scripts. That patch sat in front of every request on
       every page for every app on the store, and the theme already publishes
       cartUpdate for each of its own cart writes (cart-items, product-form,
       product-bundle, quick-order-list, gift-wrapping), so hookPubSub covers the
       cases that can actually orphan a reward. It is not coming back. */

    scheduleSync: debounce(function () { PWOffers.syncCart(); }, 400),

    /* Reconcile only: drop rewards whose paid item is gone, cap quantities.
       Never adds — see the header note. */
    syncCart: function () {
      var self = this;
      if (self._locked) {
        self._pendingSync = true;
        return Promise.resolve();
      }
      self._locked = true;

      return getCart().then(function (cart) {
        var activeOfferId = self.getActiveOfferId(cart);
        var currentConfig = readDataIsland() || getCachedOfferConfig(activeOfferId);

        var hasAutoItems = cart.items.some(function (item) {
          return item.properties && item.properties._pw_auto_item === 'true';
        });
        hlog('sync: activeOffer=' + activeOfferId,
          'config=' + (currentConfig ? currentConfig.offer_id : 'none'),
          'autoItemsInCart=' + hasAutoItems, 'cartItems=' + cart.items.length);
        if (!hasAutoItems) return; // nothing to reconcile

        return removeOrphanedAutoItems(cart, currentConfig)
          .then(function (removedKeys) {
            return normalizeAutoItems(cart, removedKeys, currentConfig);
          });
      }).then(function () {
        /* Only re-render when this pass actually changed the cart. */
        if (self._dirty) {
          self._dirty = false;
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        }
      }).catch(function (e) {
        console.error('[PWOffers] sync failed', e);
      }).then(function () {
        self._locked = false;
        if (self._pendingSync) {
          self._pendingSync = false;
          self.scheduleSync();
        }
      });
    },

    /* ---- bundle add-to-cart (fixed-component offers) ---- */

    addBundleToCart: function (button, offerId, componentVariantIds, quantity) {
      var qty = parseInt(quantity, 10) || 1;
      if (button && button.disabled) return Promise.resolve();
      if (button) button.disabled = true;

      var self = this;
      return getCart().then(function (cart) {
        var alreadyAdded = cart.items.some(function (item) {
          return item.properties &&
            item.properties._pw_offer_id === offerId &&
            item.properties._pw_offer_role === 'bundle_component';
        });
        if (alreadyAdded) return null;

        return addItems(componentVariantIds.map(function (variantId) {
          return {
            id: variantId,
            quantity: qty,
            properties: {
              _pw_auto_item: 'true',
              _pw_offer_id: offerId,
              _pw_offer_role: 'bundle_component'
            }
          };
        }));
      }).then(function () {
        return self.refreshCartUI(button);
      }).catch(function () {
        self.showMessage('One of the bundle items is currently unavailable. Please try again shortly.');
      }).then(function () {
        if (button) button.disabled = false;
      });
    },

    /* ---- direct-checkout (cart permalink) flow ----
       Builds /cart/{id}:{qty},... which REPLACES the cart contents and lands
       the customer straight in checkout with the whole offer pre-loaded.
       Shopify automatic discounts apply at checkout as usual. Kept for custom
       "Buy now" buttons — the collection grid does NOT use this. */

    clearActiveOffer: function () {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch (e) { /* ignore */ }
      var attrs = {};
      attrs[CART_ATTR_KEY] = '';
      return updateCartAttributes(attrs).catch(function () { /* non-fatal */ });
    },

    _goToPermalink: function (parts) {
      var base = ROOT.replace(/\/$/, '');
      /* Clear the offer marker first so the background sync can never
         double-add rewards if the customer backs out of checkout. */
      this.clearActiveOffer().then(function () {
        window.location.href = base + '/cart/' + parts.join(',');
      });
    },

    buyNow: function (variantId) {
      var self = this;
      var config = readDataIsland() || getCachedOfferConfig(self.getActiveOfferId());
      hlog('buyNow', variantId, 'config=', config ? config.offer_id : 'none');
      if (!config) {
        self._goToPermalink([variantId + ':1']);
        return;
      }

      var qty = 1;
      if (config.same_variant_gift) qty += config.same_variant_gift_quantity || 1;
      var parts = [variantId + ':' + qty];

      if (!config.inventory_gift_enabled) {
        self._goToPermalink(parts);
        return;
      }

      refreshDataIsland(config).then(function (fresh) {
        var pool = availableGiftPool(config, fresh);
        if (pool[0]) {
          hlog('buyNow: gift selected', pool[0].id);
          parts.push(pool[0].id + ':' + (config.inventory_gift_quantity || 1));
        } else {
          self.showMessage(config.unavailable_message || 'The complimentary gift is currently unavailable.');
        }
        self._goToPermalink(parts);
      });
    },

    buyBundleNow: function (componentVariantIds, quantity) {
      var qty = parseInt(quantity, 10) || 1;
      var parts = componentVariantIds.map(function (id) { return id + ':' + qty; });
      hlog('buyBundleNow', parts.join(','));
      this._goToPermalink(parts);
    },

    /* ---- non-blocking error surface ---- */

    showMessage: function (text) {
      var el = document.getElementById('PWOffersMessage');
      if (!el) {
        el = document.createElement('div');
        el.id = 'PWOffersMessage';
        el.setAttribute('role', 'status');
        el.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);' +
          'background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:8px;z-index:9999;' +
          'font-size:14px;max-width:90vw;box-shadow:0 4px 12px rgba(0,0,0,.25);';
        document.body.appendChild(el);
      }
      el.textContent = text;
      el.style.display = 'block';
      clearTimeout(el.__hideTimer);
      el.__hideTimer = setTimeout(function () { el.style.display = 'none'; }, 5000);
    }
  };

  window.PWOffers = PWOffers;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { PWOffers.init(); });
  } else {
    PWOffers.init();
  }
})();
